// Minimal promise wrapper over IndexedDB (ENG-9). Hand-rolled — no Dexie (§1).
// The sim defines the data shapes; this is the browser persistence adapter.

export const DB_NAME = 'terraforge';
// v2 (Part VII / TAL-4): the one Part-VII IDB bump — adds the `tileCache` +
// `overlays` stores (TIL-4 / OVL-1) and marks the save-format v2 era. New stores
// are created idempotently in onupgradeneeded, so a v1 DB upgrades in place.
const DB_VERSION = 2;

/** Object stores (ARCH §3.6). blockEntities/entities/maps/atlasCache are created
 *  for schema stability; tileCache/overlays arrive with Part VII (v1.3). Only
 *  worlds/player/sections are used before M6/M8. */
export const STORES = ['worlds', 'player', 'sections', 'blockEntities', 'entities', 'maps', 'atlasCache', 'tileCache', 'overlays'] as const;
export type StoreName = (typeof STORES)[number];

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of STORES) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function idbGet<T>(db: IDBDatabase, store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return promisify(db.transaction(store, 'readonly').objectStore(store).get(key) as IDBRequest<T | undefined>);
}

export function idbGetAll<T>(db: IDBDatabase, store: StoreName, range?: IDBKeyRange): Promise<T[]> {
  return promisify(db.transaction(store, 'readonly').objectStore(store).getAll(range) as IDBRequest<T[]>);
}

export function idbGetAllKeys(db: IDBDatabase, store: StoreName, range?: IDBKeyRange): Promise<IDBValidKey[]> {
  return promisify(db.transaction(store, 'readonly').objectStore(store).getAllKeys(range));
}

export function idbPut(db: IDBDatabase, store: StoreName, key: IDBValidKey, value: unknown): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).put(value, key);
  return txDone(tx);
}

/** Put many (key,value) pairs in ONE transaction (autosave writes all sections). */
export function idbPutMany(db: IDBDatabase, store: StoreName, entries: Array<[IDBValidKey, unknown]>): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  const os = tx.objectStore(store);
  for (const [k, v] of entries) os.put(v, k);
  return txDone(tx);
}

export function idbDelete(db: IDBDatabase, store: StoreName, key: IDBValidKey): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(key);
  return txDone(tx);
}

/** Delete every key in `range` from `store`. */
export function idbClearRange(db: IDBDatabase, store: StoreName, range: IDBKeyRange): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(range);
  return txDone(tx);
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
